import { useState, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

// ─── ANIMATED GRID BACKGROUND ────────────────────────────────────────────────
function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(232,96,42,0.15) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(232,96,42,0.12) 0%, transparent 70%)" }} />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(200,74,30,0.1) 0%, transparent 70%)" }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(232,96,42,0.04) 0%, transparent 70%)" }} />
    </div>
  );
}

// ─── FLOATING ORBS ───────────────────────────────────────────────────────────
function FloatingOrbs() {
  const orbs = [
    { size: 6, top: "18%", left: "12%",  delay: 0,    color: "rgba(232,96,42,0.5)" },
    { size: 4, top: "65%", left: "6%",   delay: 800,  color: "rgba(232,96,42,0.4)" },
    { size: 8, top: "42%", right: "9%",  delay: 400,  color: "rgba(200,74,30,0.4)" },
    { size: 3, top: "80%", right: "14%", delay: 1200, color: "rgba(232,96,42,0.3)" },
    { size: 5, top: "10%", right: "25%", delay: 600,  color: "rgba(232,96,42,0.25)" },
  ];
  return (
    <>
      {orbs.map((o, i) => (
        <div key={i}
          className="absolute rounded-full pointer-events-none animate-pulse"
          style={{
            width: o.size, height: o.size,
            top: o.top, left: o.left, right: o.right,
            background: o.color,
            animationDelay: `${o.delay}ms`,
            animationDuration: "3s",
            boxShadow: `0 0 ${o.size * 3}px ${o.color}`,
          }} />
      ))}
    </>
  );
}

// ─── CUSTOM INPUT ────────────────────────────────────────────────────────────
function Field({ label, type, value, onChange, placeholder, icon, autoFocus }) {
  const [focused, setFocused] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const isPwd = type === "password";
  const inputType = isPwd ? (showPwd ? "text" : "password") : type;

  return (
    <div className="flex flex-col gap-1.5 md:gap-1">
      <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-stone-500">{label}</label>
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10">
          <i className={`ti ${icon} text-sm transition-all duration-200 ${focused ? "text-orange-400" : "text-stone-600"}`} />
        </div>
        <input
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          required
          className="w-full pl-11 pr-11 py-3.5 md:py-3 rounded-xl text-sm text-white placeholder-[#8a8a8a] outline-none transition-all duration-200"
          style={{
            background: focused ? "rgba(255,255,255,0.055)" : "rgba(255,255,255,0.028)",
            border: `1px solid ${focused ? "rgba(232,96,42,0.55)" : "rgba(255,255,255,0.07)"}`,
            boxShadow: focused ? "0 0 0 3px rgba(232,96,42,0.1), inset 0 1px 0 rgba(255,255,255,0.04)" : "inset 0 1px 0 rgba(255,255,255,0.02)",
          }}
        />
        {isPwd && (
          <button type="button" onClick={() => setShowPwd(p => !p)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-700 hover:text-stone-400 transition-colors z-10">
            <i className={`ti ${showPwd ? "ti-eye-off" : "ti-eye"} text-sm`} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── CUSTOM CHECKBOX ─────────────────────────────────────────────────────────
function Checkbox({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer group select-none" onClick={() => onChange(!checked)}>
      <div className="w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0 transition-all duration-200"
        style={{
          background: checked ? "linear-gradient(135deg,#e8602a,#c84a1e)" : "transparent",
          border: `1.5px solid ${checked ? "#e8602a" : "rgba(255,255,255,0.18)"}`,
          boxShadow: checked ? "0 0 8px rgba(232,96,42,0.4)" : "none",
        }}>
        {checked && <i className="ti ti-check text-white" style={{ fontSize: 9 }} />}
      </div>
      <span className="text-xs text-stone-500 group-hover:text-stone-300 transition-colors">{label}</span>
    </label>
  );
}

// ─── SUBSCRIPTION PLAN SELECTOR (Step 2) ────────────────────────────────────
// Redesigned as a compact, stacked list (rather than a 3-up grid) so it never
// overflows the narrow form column on desktop and stays tidy on mobile.
function PlanSelector({ plans, selectedId, onChange, isTrial, onTrialChange }) {
  const selectedPlan = plans.find(p => p.id === selectedId);

  return (
    <div className="flex flex-col gap-3.5 w-full min-w-0 pt-20">
      {/* Header */}
      <div className="text-center mb-0.5">
        <h2 className="text-xl md:text-[22px] font-extrabold text-white tracking-tight leading-tight mb-1">
          Choose your plan
        </h2>
        <p className="text-[12px] text-stone-500">
          Select the perfect plan for your business needs
        </p>
      </div>

      {/* Trial toggle */}
      <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl cursor-pointer"
        onClick={() => onTrialChange(!isTrial)}
        style={{ background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.25)" }}>
        <div className="flex items-center gap-2 min-w-0">
          <i className="ti ti-gift text-emerald-400 text-base flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-[13px] text-emerald-300 font-bold truncate">14-Day Free Trial</div>
            <div className="text-[10px] text-emerald-400/60 truncate">No credit card required</div>
          </div>
        </div>
        <div className="relative w-10 h-[22px] rounded-full transition-all flex-shrink-0"
          style={{ background: isTrial ? "#34d399" : "rgba(255,255,255,0.15)" }}>
          <div className="absolute top-0.5 w-[18px] h-[18px] bg-white rounded-full transition-all shadow-lg"
            style={{ left: isTrial ? "calc(100% - 20px)" : "2px" }} />
        </div>
      </div>

      {/* Plan cards — single column, compact rows */}
      <div className="flex flex-col gap-2.5 w-full min-w-0">
        {plans.map(plan => {
          const selected = selectedId === plan.id;
          return (
            <div key={plan.id} onClick={() => onChange(plan.id)}
              className="cursor-pointer rounded-2xl p-3 md:p-3.5 transition-all duration-200 border-2 relative w-full min-w-0 overflow-hidden group"
              style={{
                background: selected
                  ? "linear-gradient(135deg, rgba(232,96,42,0.15), rgba(200,74,30,0.1))"
                  : "rgba(255,255,255,0.035)",
                borderColor: selected ? plan.color : "rgba(255,255,255,0.1)",
                boxShadow: selected ? `0 0 0 3px ${plan.color}20` : "none",
              }}>

              <div className="flex items-start gap-2.5 min-w-0">
                {/* Icon */}
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110"
                  style={{ background: `${plan.color}20`, border: `1px solid ${plan.color}30` }}>
                  <i className={`ti ${plan.icon} text-base`} style={{ color: plan.color }} />
                </div>

                {/* Name / price / description */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                      <span className="text-[14px] font-extrabold text-white leading-tight">{plan.name}</span>
                      {plan.popular && (
                        <span className="px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider flex-shrink-0"
                          style={{ background: "linear-gradient(135deg,#e8602a,#c84a1e)", color: "white" }}>
                          Popular
                        </span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-0.5 flex-shrink-0 whitespace-nowrap">
                      <span className="text-base font-black" style={{ color: plan.color }}>{plan.price}</span>
                      <span className="text-[10px] text-stone-600">{plan.period}</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5 truncate">{plan.description}</div>
                </div>

                {/* Selection radio */}
                <div className="w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 mt-0.5"
                  style={{
                    borderColor: selected ? plan.color : "rgba(255,255,255,0.2)",
                    background: selected ? plan.color : "transparent",
                  }}>
                  {selected && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                </div>
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-1.5 mt-2.5 pl-[46px]">
                {plan.features.map((feature, i) => (
                  <span key={i}
                    className="inline-flex items-center gap-1 text-[10px] text-stone-400 px-2 py-1 rounded-md leading-tight"
                    style={{ background: "rgba(255,255,255,0.05)" }}>
                    <i className="ti ti-check text-[9px] flex-shrink-0" style={{ color: plan.color }} />
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {isTrial && selectedPlan && (
        <div className="text-center px-4 py-2.5 rounded-xl"
          style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.05)" }}>
          <div className="text-[11px] text-stone-500">
            Start with <span className="text-emerald-400 font-bold">14 days free</span>, then{" "}
            <span className="text-white font-bold">{selectedPlan.price}{selectedPlan.period}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FEATURE CARD (desktop brand panel) ──────────────────────────────────────
function FeatureCard({ icon, label, desc }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-2xl transition-all duration-300 hover:scale-[1.02] cursor-default group"
      style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)" }}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110"
        style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.1)" }}>
        <i className={`ti ${icon} text-white text-base`} />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold text-white leading-tight">{label}</div>
        <div className="text-[13px] text-white/80 mt-0.5 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

// ─── STAT PILL ───────────────────────────────────────────────────────────────
function StatPill({ value, label }) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-5 py-3 rounded-2xl"
      style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
      <span className="text-xl font-extrabold text-white leading-none">{value}</span>
      <span className="text-[10px] text-white/50 whitespace-nowrap">{label}</span>
    </div>
  );
}

// ─── MOBILE FEATURE CHIP ─────────────────────────────────────────────────────
function MobileFeatureChip({ icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
      style={{ background: "rgba(232,96,42,0.12)", border: "1px solid rgba(232,96,42,0.2)" }}>
      <i className={`ti ${icon} text-orange-400 text-xs`} />
      <span className="text-[11px] font-semibold text-orange-300">{label}</span>
    </div>
  );
}

// ─── MOBILE STAT BADGE ───────────────────────────────────────────────────────
function MobileStatBadge({ value, label }) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-4 py-2 rounded-xl"
      style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <span className="text-base font-extrabold text-white leading-none">{value}</span>
      <span className="text-[9px] text-stone-500 whitespace-nowrap">{label}</span>
    </div>
  );
}

// ─── BRAND MARK (used both on the form logo and the brand-panel logo) ────────
function BrandMark({ size = "w-14 h-14", iconSize = "text-2xl" }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 rounded-2xl scale-110 blur-xl opacity-70"
        style={{ background: "linear-gradient(135deg,#e8602a,#c84a1e)" }} />
      <div className={`relative ${size} rounded-2xl flex items-center justify-center`}
        style={{
          background: "linear-gradient(135deg,#e8602a,#c84a1e)",
          boxShadow: "0 12px 36px rgba(232,96,42,0.5), 0 0 0 1px rgba(255,255,255,0.08)",
        }}>
        <i className={`ti ti-bolt text-white ${iconSize}`} />
      </div>
    </div>
  );
}

// ─── COMPANY LINK (single consistent name + hyperlink everywhere) ───────────
const COMPANY_URL = "https://awktechz.com/";
function CompanyLink({ className }) {
  return (
    <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer"
      className={`hover:text-orange-400 transition-colors ${className || ""}`}>
      AWK Technologies
    </a>
  );
}

// ─── FEATURE DATA ────────────────────────────────────────────────────────────
const features = [
  { icon: "ti-robot",      label: "Autonomous AI Agent",  desc: "Responds 24/7 on your behalf across every channel" },
  { icon: "ti-chart-line", label: "Smart Lead Scoring",   desc: "Prioritize hot leads with real-time AI scoring" },
  { icon: "ti-messages",   label: "Omnichannel Inbox",    desc: "WhatsApp, Facebook, Instagram — all in one place" },
  { icon: "ti-sparkles",   label: "Conversation Intel",   desc: "Deep insights into every customer interaction" },
];

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode]         = useState("login"); // "login" or "register"
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");

  // Sync mode with URL
  useEffect(() => {
    if (location.pathname === '/register') {
      setMode('register');
    } else {
      setMode('login');
    }
  }, [location.pathname]);

  // Handle mode changes with URL navigation
  const handleModeChange = (newMode) => {
    setMode(newMode);
    setRegistrationStep(1); // Reset to step 1 when changing modes
    setError(""); // Clear any errors
    navigate(newMode === 'register' ? '/register' : '/login');
  };
  const [remember, setRemember] = useState(false);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);

  // Register form fields
  const [businessName, setBusinessName] = useState("");
  const [fullName, setFullName] = useState("");
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("STARTER-PLAN-ID"); // Default to Starter plan - REPLACE WITH REAL ID!
  const [isTrial, setIsTrial] = useState(true);

  // Registration step (1 = basic info, 2 = plan selection)
  const [registrationStep, setRegistrationStep] = useState(1);
  
  // Available subscription plans (will be fetched from API or use these defaults)
  const [availablePlans, setAvailablePlans] = useState([
    // TEMPORARY: These IDs need to be replaced with actual IDs from your database
    // Check your database SubscriptionPlans table for the real UUIDs
    { id: "STARTER-PLAN-ID", name: "Starter", price: "$29", period: "/month", description: "Perfect for small businesses", features: ["Up to 1,000 conversations", "2 team members", "Basic analytics", "Email support"], icon: "ti-rocket", color: "#5b8dee" },
    { id: "PRO-PLAN-ID", name: "Pro", price: "$79", period: "/month", description: "For growing teams", features: ["Up to 10,000 conversations", "10 team members", "Advanced analytics", "Priority support"], icon: "ti-chart-line", color: "#e8602a", popular: true },
    { id: "ENTERPRISE-PLAN-ID", name: "Enterprise", price: "Custom", period: "", description: "For large organizations", features: ["Unlimited conversations", "Unlimited team members", "Custom integrations", "Dedicated support"], icon: "ti-building", color: "#a855f7" },
  ]);

  // Fetch available subscription plans from API when component mounts
  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || "";
    
    // Fetch plans from the correct API endpoint
    fetch(`${API_URL}/api/plans`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          console.log("Plans API not available, using default plans");
          return null;
        }
        const data = await response.json();
        return data;
      })
      .then((result) => {
        if (result && Array.isArray(result)) {
          // Map API plans to our format
          const mappedPlans = result.map((plan, index) => ({
            id: plan.id,
            name: plan.name,
            price: plan.currency === "USD" ? `$${plan.price}` : `${plan.price} ${plan.currency}`,
            period: plan.billingCycle === "Monthly" ? "/month" : plan.billingCycle === "Yearly" ? "/year" : "",
            description: plan.description || `${plan.name} plan`,
            features: plan.features ? plan.features.split(',').map(f => f.trim()) : [
              `${plan.name} features`,
              "Team collaboration",
              "Analytics dashboard",
              "Support included"
            ],
            icon: index === 0 ? "ti-rocket" : index === 1 ? "ti-chart-line" : "ti-building",
            color: index === 0 ? "#5b8dee" : index === 1 ? "#e8602a" : "#a855f7",
            popular: index === 1, // Mark middle plan as popular
          }));
          
          if (mappedPlans.length > 0) {
            setAvailablePlans(mappedPlans);
            // Set first plan as default
            setSubscriptionPlanId(mappedPlans[0].id);
            console.log("✅ Loaded subscription plans from API:", mappedPlans);
          }
        } else if (result && result.data && Array.isArray(result.data)) {
          // Handle if API returns wrapped response
          const mappedPlans = result.data.map((plan, index) => ({
            id: plan.id,
            name: plan.name,
            price: plan.currency === "USD" ? `$${plan.price}` : `${plan.price} ${plan.currency}`,
            period: plan.billingCycle === "Monthly" ? "/month" : plan.billingCycle === "Yearly" ? "/year" : "",
            description: plan.description || `${plan.name} plan`,
            features: plan.features ? plan.features.split(',').map(f => f.trim()) : [
              `${plan.name} features`,
              "Team collaboration",
              "Analytics dashboard",
              "Support included"
            ],
            icon: index === 0 ? "ti-rocket" : index === 1 ? "ti-chart-line" : "ti-building",
            color: index === 0 ? "#5b8dee" : index === 1 ? "#e8602a" : "#a855f7",
            popular: index === 1,
          }));
          
          if (mappedPlans.length > 0) {
            setAvailablePlans(mappedPlans);
            setSubscriptionPlanId(mappedPlans[0].id);
            console.log("✅ Loaded subscription plans from API:", mappedPlans);
          }
        }
      })
      .catch((error) => {
        console.log("⚠️ Could not fetch plans from API, using defaults:", error.message);
      });
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("nigella_remembered_email");
    if (saved) { setEmail(saved); setRemember(true); }
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    if (loading || success) return;
    setError("");

    if (mode === "register") {
      // Step 1: Validate basic info and go to step 2
      if (registrationStep === 1) {
        // Validate required fields
        if (!businessName.trim()) {
          setError("Please enter your business name");
          return;
        }
        if (!fullName.trim()) {
          setError("Please enter your full name");
          return;
        }
        if (!email.trim()) {
          setError("Please enter your email address");
          return;
        }
        if (!password.trim() || password.length < 6) {
          setError("Password must be at least 6 characters");
          return;
        }

        // Move to step 2 (plan selection)
        setRegistrationStep(2);
        return;
      }

      // Step 2: Submit registration
      setLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || "";
      
      fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessName: businessName,
          fullName: fullName,
          email: email,
          password: password,
          subscriptionPlanId: subscriptionPlanId,
          isTrial: isTrial,
        }),
      })
        .then(async (response) => {
          const data = await response.json();

          if (!response.ok) {
            // Enhanced error handling - show detailed API error
            console.error("Registration API Error:", data);
            let errorMsg = data.message || "Registration failed";
            
            // If there are validation errors, show them
            if (data.errors) {
              const errorDetails = Object.values(data.errors).flat().join(". ");
              errorMsg = errorDetails || errorMsg;
            }
            
            throw new Error(errorMsg);
          }

          return data;
        })
        .then((result) => {
          console.log("Registration successful:", result);
          setSuccess(true);
          setTimeout(() => {
            // After successful registration, switch to login mode
            setMode("login");
            setRegistrationStep(1); // Reset to step 1
            setSuccess(false);
            setLoading(false);
            setError("");
            // Keep email filled
          }, 1500);
        })
        .catch((error) => {
          console.error("Registration error:", error);
          let errorMessage = "";
          if (error.message === "Failed to fetch" || error.name === "TypeError") {
            errorMessage = "Cannot connect to server. Please contact your system administrator.";
          } else {
            errorMessage = error.message || "Registration failed. Please try again.";
          }
          setError(errorMessage);
          setLoading(false);
        });
    } else {
      // Call actual login API
      // Use environment variable for API URL
      const API_URL = process.env.REACT_APP_API_URL || "";
      fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          password: password,
          rememberMe: remember,
        }),
      })
        .then(async (response) => {
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || "Login failed");
          }

          return data;
        })
        .then((result) => {
          // API returns ApiResponse<T> wrapper, extract data
          const loginData = result.data || result;

          // Store token in localStorage
          if (loginData.token) {
            localStorage.setItem("token", loginData.token);

            // Also store email if remember me is checked
            if (remember) {
              localStorage.setItem("nigella_remembered_email", email);
            } else {
              localStorage.removeItem("nigella_remembered_email");
            }

            // Check if user is SuperAdmin or TenantAdmin based on roles
            const isSuperAdmin = loginData.roles && loginData.roles.includes("SuperAdmin");

            setSuccess(true);
            setTimeout(() => {
              onLogin(isSuperAdmin ? "super-admin" : "dashboard");
            }, 800);
          } else {
            throw new Error("No token received from server");
          }
        })
        .catch((error) => {
          console.error("Login error:", error);
          // More detailed error for debugging
          let errorMessage = "";
          if (error.message === "Failed to fetch" || error.name === "TypeError") {
            errorMessage = "Cannot connect to server. This is likely a CORS or network issue. Please contact your system administrator.";
          } else {
            errorMessage = error.message || "Invalid email or password. Please check your credentials and try again.";
          }
          setError(errorMessage);
          setLoading(false);
        });
    }
  }

  const isPlanStep = mode === "register" && registrationStep === 2;

  // ── Shared form content (used in both mobile & desktop) ──────────────────
  const FormContent = (
    <>
      {/* Logo + brand - 80px padding top */}
      <div className="flex flex-col items-center mb-5 md:mb-4 pt-20">
        <div className="mb-3 md:mb-2.5">
          <BrandMark size="w-12 h-12 md:w-11 md:h-11" iconSize="text-xl md:text-lg" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-orange-500 uppercase tracking-[0.15em]">Nigella Vox</span>
        </div>
      </div>

      {/* Mobile brand strip — shown only on mobile above the form (hidden during the plan step to save space) */}
      {!isPlanStep && (
        <div className="flex md:hidden flex-col items-center mb-5 text-center">
          {/* Mini stat row */}
          <div className="flex items-center gap-2 mb-4">
            <MobileStatBadge value="24/7" label="Always Active" />
            <MobileStatBadge value="98%"  label="Response Rate" />
            <MobileStatBadge value="3×"   label="Conversion" />
          </div>
          {/* Feature chips */}
          <div className="flex flex-wrap justify-center gap-2">
            {features.map(f => <MobileFeatureChip key={f.label} icon={f.icon} label={f.label} />)}
          </div>
          {/* Divider */}
          <div className="flex items-center gap-3 mt-5 w-full">
            <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
            <span className="text-[10px] font-semibold text-stone-700 uppercase tracking-widest">{mode === "login" ? "sign in below" : "register below"}</span>
            <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
          </div>
        </div>
      )}

      {/* Mode Toggle - only show on login page */}
      {mode === "login" && (
        <div className="flex gap-2 mb-6 md:mb-4">
          <button type="button" onClick={() => handleModeChange("login")}
            className="flex-1 py-2.5 md:py-2 rounded-xl text-sm font-bold transition-all"
            style={{
              background: mode === "login" ? "rgba(232,96,42,0.15)" : "transparent",
              border: `1px solid ${mode === "login" ? "rgba(232,96,42,0.35)" : "rgba(255,255,255,0.1)"}`,
              color: mode === "login" ? "#e8602a" : "#8a8a8a",
            }}>
            Sign In
          </button>
          <button type="button" onClick={() => handleModeChange("register")}
            className="flex-1 py-2.5 md:py-2 rounded-xl text-sm font-bold transition-all"
            style={{
              background: mode === "register" ? "rgba(232,96,42,0.15)" : "transparent",
              border: `1px solid ${mode === "register" ? "rgba(232,96,42,0.35)" : "rgba(255,255,255,0.1)"}`,
              color: mode === "register" ? "#e8602a" : "#8a8a8a",
            }}>
            Register
          </button>
        </div>
      )}

      {/* Registration Step Indicator */}
      {mode === "register" && (
        <div className="flex items-center justify-center gap-2 mb-4">
          {[1, 2].map(step => (
            <div key={step} className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all flex-shrink-0"
                  style={{
                    background: registrationStep >= step ? "linear-gradient(135deg,#e8602a,#c84a1e)" : "rgba(255,255,255,0.05)",
                    color: registrationStep >= step ? "white" : "#6b7280",
                    border: `1.5px solid ${registrationStep >= step ? "#e8602a" : "rgba(255,255,255,0.1)"}`,
                  }}>
                  {step}
                </div>
                <span className="text-[11px] font-semibold whitespace-nowrap" style={{ color: registrationStep >= step ? "#e8602a" : "#6b7280" }}>
                  {step === 1 ? "Account" : "Plan"}
                </span>
              </div>
              {step < 2 && (
                <div className="w-8 h-0.5 rounded-full mx-1 flex-shrink-0"
                  style={{ background: registrationStep > step ? "#e8602a" : "rgba(255,255,255,0.1)" }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Heading */}
      {!isPlanStep && (
        <div className="text-center mb-5 md:mb-4">
          {mode === "register" && registrationStep === 2 ? (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setRegistrationStep(1)}
                className="flex items-center gap-1.5 text-[12px] text-stone-500 hover:text-orange-400 transition-colors mx-auto">
                <i className="ti ti-arrow-left text-sm" />
                Back to account details
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl md:text-[26px] font-extrabold text-white tracking-tight leading-tight mb-1.5 md:mb-1">
                {mode === "login" ? "Welcome back" : "Create your account"}
              </h1>
              <p className="text-[13px] text-stone-500">
                {mode === "login" ? "Sign in to your workspace to continue" : "Start your AI-powered conversation journey"}
              </p>
            </>
          )}
        </div>
      )}

      {/* Back button lives above the plan selector's own heading during step 2 */}
      {isPlanStep && (
        <div className="mb-3 -mt-1">
          <button
            type="button"
            onClick={() => setRegistrationStep(1)}
            className="flex items-center gap-1.5 text-[12px] text-stone-500 hover:text-orange-400 transition-colors mx-auto">
            <i className="ti ti-arrow-left text-sm" />
            Back to account details
          </button>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 md:gap-3 w-full min-w-0">
        {mode === "register" && registrationStep === 1 && (
          <>
            <Field label="Business Name" type="text" value={businessName} onChange={setBusinessName}
              placeholder="Your Company Name" icon="ti-building-store" autoFocus />
            <Field label="Full Name" type="text" value={fullName} onChange={setFullName}
              placeholder="John Doe" icon="ti-user" />
            <Field label="Email Address" type="email" value={email} onChange={setEmail}
              placeholder="you@company.com" icon="ti-mail" />
            <Field label="Password" type="password" value={password} onChange={setPassword}
              placeholder="Create a strong password" icon="ti-lock" />
          </>
        )}

        {mode === "register" && registrationStep === 2 && (
          <PlanSelector
            plans={availablePlans}
            selectedId={subscriptionPlanId}
            onChange={setSubscriptionPlanId}
            isTrial={isTrial}
            onTrialChange={setIsTrial}
          />
        )}

        {mode === "login" && (
          <>
            <Field label="Email Address" type="email" value={email} onChange={setEmail}
              placeholder="you@company.com" icon="ti-mail" autoFocus />
            <Field label="Password" type="password" value={password} onChange={setPassword}
              placeholder="Enter your password" icon="ti-lock" />
            <div className="flex items-center justify-between">
              <Checkbox checked={remember} onChange={setRemember} label="Remember me" />
              <button type="button"
                className="text-[12px] font-semibold text-orange-500 hover:text-orange-400 transition-colors">
                Forgot password?
              </button>
            </div>
          </>
        )}

        {error && (
          <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-xl"
            style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.22)" }}>
            <i className="ti ti-alert-circle text-red-400 text-sm mt-0.5 flex-shrink-0" />
            <span className="text-[12px] text-red-400 leading-relaxed">{error}</span>
          </div>
        )}

        <button type="submit" disabled={loading || success}
          className="relative w-full py-4 md:py-3 rounded-xl text-sm font-bold text-white overflow-hidden transition-all duration-300 mt-0.5 group active:scale-[0.98]"
          style={{
            background: success
              ? "linear-gradient(135deg,#34d399,#059669)"
              : "linear-gradient(135deg,#e8602a,#c84a1e)",
            boxShadow: success
              ? "0 8px 24px rgba(52,211,153,0.35)"
              : "0 8px 28px rgba(232,96,42,0.45)",
          }}>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.07) 50%, transparent 100%)" }} />
          {loading ? (
            <span className="flex items-center justify-center gap-2.5">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {mode === "register" && registrationStep === 2 ? "Creating account…" : "Signing in…"}
            </span>
          ) : success ? (
            <span className="flex items-center justify-center gap-2.5">
              <i className="ti ti-circle-check text-lg" />
              {mode === "register" ? "Account created! Redirecting…" : "Authenticated! Redirecting…"}
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2.5">
              <i className={`ti ${mode === "register" ? (registrationStep === 1 ? "ti-arrow-right" : "ti-user-plus") : "ti-login"} text-base`} />
              {mode === "register"
                ? (registrationStep === 1 ? "Next: Choose Plan" : "Create Account")
                : "Sign In to Workspace"}
            </span>
          )}
        </button>
      </form>

      {/* Sign up / Sign in toggle */}
      <p className="text-center text-[12px] text-stone-600 mt-5 md:mt-4">
        {mode === "login" ? (
          <>
            Don't have an account?{" "}
            <button onClick={() => handleModeChange("register")} className="text-orange-500 hover:text-orange-400 font-bold transition-colors">
              Register now
            </button>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <button onClick={() => setMode("login")} className="text-orange-500 hover:text-orange-400 font-bold transition-colors">
              Sign in
            </button>
          </>
        )}
      </p>

      {/* Footer links */}
      <div className="flex items-center justify-center gap-3 mt-6 md:mt-4 pt-5 md:pt-3 flex-wrap"
        style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        <span className="text-[10px] text-stone-400">© 2026 <CompanyLink /></span>
        {["Privacy", "Terms", "Support"].map((l) => (
          <span key={l} className="flex items-center gap-3">
            <span className="w-0.5 h-0.5 rounded-full bg-stone-600" />
            <button className="text-[10px] text-stone-400 hover:text-stone-200 transition-colors">{l}</button>
          </span>
        ))}
      </div>
    </>
  );

  // ── Brand panel content (shared shape for the right-hand marketing side) ─
  const BrandPanel = (
    <div className="flex-1 relative overflow-hidden flex items-center justify-center">
      <div className="absolute inset-0"
        style={{ background: "linear-gradient(150deg, #1c0e07 0%, #2a1208 35%, #9c3d12 75%, #e8602a 100%)" }} />
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px]"
          style={{ background: "radial-gradient(circle at 85% 15%, rgba(255,180,100,0.25) 0%, transparent 55%)", transform: "translate(10%,-10%)" }} />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px]"
          style={{ background: "radial-gradient(circle at 15% 85%, rgba(232,96,42,0.35) 0%, transparent 55%)", transform: "translate(-10%,10%)" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px]"
          style={{ background: "radial-gradient(circle, rgba(255,140,60,0.08) 0%, transparent 70%)" }} />
      </div>
      <div className="absolute inset-0 opacity-[0.06]"
        style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <div className="absolute inset-0 opacity-[0.025]"
        style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0px, #fff 1px, transparent 1px, transparent 22px)" }} />
      <div className="absolute top-12 right-12 w-32 h-32 rounded-full pointer-events-none"
        style={{ border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.01)" }} />
      <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full pointer-events-none"
        style={{ border: "1px solid rgba(255,255,255,0.05)" }} />
      <div className="absolute top-1/3 left-6 w-20 h-20 rounded-full pointer-events-none"
        style={{ border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }} />

      <div className="relative z-10 flex flex-col items-center text-center px-10 max-w-lg w-full">
        <div className="mb-7">
          <BrandMark size="w-20 h-20" iconSize="text-4xl" />
        </div>
        <div className="mb-2 leading-none">
          <div className="text-[52px] font-black text-white tracking-[-2px] leading-none"
            style={{ textShadow: "0 4px 24px rgba(0,0,0,0.3)" }}>NIGELLA</div>
          <div className="text-[52px] font-black tracking-[-2px] leading-none"
            style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.65)", color: "transparent" }}>VOX</div>
        </div>
        <div className="flex items-center gap-3 mt-4 mb-5">
          <div className="h-px w-10" style={{ background: "rgba(255,255,255,0.3)" }} />
          <span className="text-[10px] font-bold text-white/60 uppercase tracking-[0.18em]">
            AI-Powered Conversation Intelligence
          </span>
          <div className="h-px w-10" style={{ background: "rgba(255,255,255,0.3)" }} />
        </div>
        <p className="text-[13px] text-white/65 leading-relaxed mb-6 max-w-sm">
          Your intelligent AI agent responds on your behalf, manages every conversation,
          scores leads automatically and optimises engagement around the clock.
        </p>
        <div className="flex items-center gap-3 mb-7">
          <StatPill value="24/7" label="Always Active" />
          <StatPill value="98%"  label="Response Rate" />
          <StatPill value="3×"   label="Lead Conversion" />
        </div>
        <div className="grid grid-cols-2 gap-2.5 w-full">
          {features.map((f) => <FeatureCard key={f.label} {...f} />)}
        </div>
        <div className="flex items-center justify-center gap-6 mt-7 pt-5 w-full"
          style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          {[
            { icon: "ti-shield-check", label: "SOC 2 Ready" },
            { icon: "ti-lock",         label: "End-to-End Encrypted" },
            { icon: "ti-server",       label: "99.9% Uptime" },
          ].map(({ icon, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <i className={`ti ${icon} text-white/60 text-xs`} />
              <span className="text-[10px] text-white/60 font-medium">{label}</span>
            </div>
          ))}
        </div>
        <div className="mt-5">
          <p className="text-[9px] text-white/25 uppercase tracking-widest mb-0.5">Developed by</p>
          <p className="text-xs font-bold text-white/60"><CompanyLink /></p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-screen overflow-x-hidden select-none"
      style={{ background: "#080707", fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif" }}>

      {/* ══════ MOBILE LAYOUT (< md) ══════ */}
      <div className="flex md:hidden min-h-screen relative overflow-hidden">
        {/* Full-screen orange-to-dark gradient bg for mobile */}
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, #0d0a09 0%, #1a0d07 50%, #0d0a09 100%)" }} />

        {/* Mobile top decorative orange bar */}
        <div className="absolute top-0 left-0 right-0 h-1"
          style={{ background: "linear-gradient(90deg, transparent, #e8602a, transparent)" }} />

        {/* Orange glow top-right */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(232,96,42,0.18) 0%, transparent 70%)" }} />
        {/* Orange glow bottom-left */}
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(200,74,30,0.12) 0%, transparent 70%)" }} />

        {/* Dot grid */}
        <div className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(232,96,42,0.08) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }} />

        {/* Floating orbs mobile */}
        <div className="absolute top-[15%] left-[8%] w-2 h-2 rounded-full animate-pulse"
          style={{ background: "rgba(232,96,42,0.5)", boxShadow: "0 0 8px rgba(232,96,42,0.5)", animationDuration: "3s" }} />
        <div className="absolute top-[70%] right-[6%] w-1.5 h-1.5 rounded-full animate-pulse"
          style={{ background: "rgba(232,96,42,0.4)", boxShadow: "0 0 6px rgba(232,96,42,0.4)", animationDelay: "1s", animationDuration: "3.5s" }} />
        <div className="absolute top-[40%] right-[12%] w-2.5 h-2.5 rounded-full animate-pulse"
          style={{ background: "rgba(200,74,30,0.35)", boxShadow: "0 0 10px rgba(200,74,30,0.35)", animationDelay: "0.5s", animationDuration: "4s" }} />

        {/* Scrollable form */}
        <div className="relative z-10 flex flex-col w-full min-h-screen">
          {/* Safe area top padding */}
          <div className="flex-1 flex flex-col items-center justify-center px-5 py-7 w-full min-w-0">
            <div className="w-full max-w-sm min-w-0">
              {FormContent}
            </div>
          </div>

          {/* Mobile bottom trust bar */}
          <div className="flex items-center justify-center gap-5 px-6 py-4 border-t"
            style={{ borderColor: "rgba(255,255,255,0.05)", background: "rgba(0,0,0,0.3)" }}>
            {[
              { icon: "ti-shield-check", label: "SOC 2 Ready" },
              { icon: "ti-lock",         label: "Encrypted" },
              { icon: "ti-server",       label: "99.9% Uptime" },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <i className={`ti ${icon} text-[10px]`} style={{ color: "rgba(232,96,42,0.6)" }} />
                <span className="text-[9px] font-medium" style={{ color: "rgba(255,255,255,0.4)" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════ DESKTOP LAYOUT (≥ md) — brand panel on the LEFT, form on the RIGHT ══════ */}
      <div className="hidden md:flex h-screen w-screen overflow-hidden">

        {/* LEFT — Brand Side */}
        {BrandPanel}

        {/* Vertical separator */}
        <div className="w-px flex-shrink-0"
          style={{ background: "linear-gradient(to bottom, transparent 0%, rgba(232,96,42,0.25) 25%, rgba(232,96,42,0.35) 50%, rgba(232,96,42,0.25) 75%, transparent 100%)" }} />

        {/* RIGHT — Form Side. Slightly wider during the plan-selection step so the
            compact plan cards get a little more breathing room, but never wide
            enough to break the balance of the layout. */}
        <div className="flex-1 flex items-center justify-center relative overflow-hidden">
          <GridBackground />
          <FloatingOrbs />
          <div className="relative z-10 w-full h-full flex items-center justify-center py-8 px-4 overflow-y-auto">
            <div className={`w-full min-w-0 transition-all duration-300 ${isPlanStep ? "max-w-[430px]" : "max-w-[380px]"}`}>
              {FormContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}